import type { Release } from '@/lib/api/schema';

interface SampleSpec {
  product: string;
  version: string;
  title: string;
  date: string;
  status: string;
  severity: string;
  breaking: boolean;
  security: boolean;
  features: number;
  bugs: number;
  deprecated: string[];
  keywords: string[];
  summary: string;
}

function build(s: SampleSpec): Release {
  return {
    releaseId: `${s.product.toLowerCase().replace(/\s+/g, '-')}_${s.version}`,
    productName: s.product,
    releaseVersion: s.version,
    releaseTitle: s.title,
    releaseDate: s.date,
    releaseStatus: s.status,
    severity: s.severity,
    targetAudience: 'All',
    breakingChanges: s.breaking,
    securityUpdate: s.security,
    featureCount: s.features,
    bugFixCount: s.bugs,
    deprecatedFeatures: s.deprecated,
    aiSummary: s.summary,
    keywords: s.keywords,
    releaseNotesUrl: 'https://help.trimble.com/',
    contentHash: '',
    lastChecked: `${s.date}T02:00:00Z`,
  };
}

/**
 * Embedded fallback data. Rendered only when the export API is unreachable
 * (for example, blocked by CORS in a static deploy) so the app never shows a
 * blank screen. Mirrors the shape of the live feed.
 */
export const SAMPLE_RELEASES: Release[] = [
  {
    product: 'Spectrum',
    version: '2025 R2',
    title: "What's New in Spectrum 2025 R2",
    date: '2025-06-30',
    status: 'GA',
    severity: 'Major',
    breaking: false,
    security: true,
    features: 7,
    bugs: 12,
    deprecated: ['Legacy report export'],
    keywords: ['security', 'api', 'dashboards'],
    summary:
      'Adds project dashboards, SSO improvements, and a security patch for the API layer.',
  },
  {
    product: 'Spectrum',
    version: '2025 R1',
    title: "What's New in Spectrum 2025 R1",
    date: '2025-01-28',
    status: 'GA',
    severity: 'Minor',
    breaking: false,
    security: false,
    features: 4,
    bugs: 9,
    deprecated: [],
    keywords: ['ui', 'performance'],
    summary: 'UI refresh across accounting modules and faster report generation.',
  },
  {
    product: 'ProContractor',
    version: '4.5.5',
    title: "What's New in ProContractor 4.5.5",
    date: '2026-03-15',
    status: 'GA',
    severity: 'Minor',
    breaking: false,
    security: false,
    features: 3,
    bugs: 5,
    deprecated: [],
    keywords: ['ui', 'bugfix'],
    summary: 'New invoice templates and resolves several reporting bugs.',
  },
  {
    product: 'ProContractor',
    version: '4.5.4',
    title: "What's New in ProContractor 4.5.4",
    date: '2025-11-10',
    status: 'GA',
    severity: 'Patch',
    breaking: false,
    security: false,
    features: 1,
    bugs: 8,
    deprecated: [],
    keywords: ['bugfix'],
    summary: 'Maintenance release fixing payroll export edge cases.',
  },
  {
    product: 'Vista',
    version: '2026.6',
    title: "What's New in Vista 2026.6",
    date: '2026-05-20',
    status: 'GA',
    severity: 'Critical',
    breaking: true,
    security: true,
    features: 6,
    bugs: 3,
    deprecated: ['Old API v1'],
    keywords: ['api', 'breaking', 'security'],
    summary:
      'Breaking API change to v2 endpoints plus a critical security fix; migration required.',
  },
  {
    product: 'Vista',
    version: '2026.3',
    title: "What's New in Vista 2026.3",
    date: '2026-02-12',
    status: 'GA',
    severity: 'Major',
    breaking: false,
    security: false,
    features: 9,
    bugs: 6,
    deprecated: [],
    keywords: ['workflow', 'reporting'],
    summary: 'Workflow automation for AP and expanded financial reporting.',
  },
  {
    product: 'Trimble Analytics',
    version: '2026 R5',
    title: "What's New in Trimble Analytics 2026 R5",
    date: '2026-05-02',
    status: 'GA',
    severity: 'Minor',
    breaking: false,
    security: false,
    features: 5,
    bugs: 2,
    deprecated: [],
    keywords: ['analytics', 'dashboards'],
    summary: 'New prebuilt dashboards and scheduled export to Power BI.',
  },
  {
    product: 'Traqspera',
    version: '2026 R9',
    title: "What's New in Traqspera 2026 R9",
    date: '2026-05-25',
    status: 'GA',
    severity: 'Minor',
    breaking: false,
    security: false,
    features: 4,
    bugs: 4,
    deprecated: [],
    keywords: ['mobile', 'field'],
    summary: 'Offline mode for field crews and timesheet improvements.',
  },
  {
    product: 'Viewpoint Field View',
    version: '2026 R4',
    title: "What's new in Field View 2026 R4",
    date: '2026-04-18',
    status: 'Preview',
    severity: 'Minor',
    breaking: false,
    security: false,
    features: 3,
    bugs: 1,
    deprecated: [],
    keywords: ['mobile', 'forms'],
    summary: 'Preview of redesigned forms engine and photo markup.',
  },
  {
    product: 'HR Management for Spectrum',
    version: '2026 R4',
    title: "What's New in HR Management for Spectrum 2026 R4",
    date: '2026-04-30',
    status: 'GA',
    severity: 'Major',
    breaking: false,
    security: true,
    features: 5,
    bugs: 3,
    deprecated: [],
    keywords: ['security', 'payroll'],
    summary: 'Payroll compliance updates and MFA enforcement for HR users.',
  },
  {
    product: 'Quadri',
    version: '2026.3',
    title: 'Quadri 2026.3 Release Notes',
    date: '2026-03-29',
    status: 'GA',
    severity: 'Minor',
    breaking: false,
    security: false,
    features: 6,
    bugs: 5,
    deprecated: [],
    keywords: ['civil', 'modeling'],
    summary: 'Improved terrain modeling and faster model sync.',
  },
  {
    product: 'Novapoint',
    version: '2026.2',
    title: 'Novapoint 2026.2 Release Notes',
    date: '2026-02-22',
    status: 'GA',
    severity: 'Patch',
    breaking: false,
    security: false,
    features: 2,
    bugs: 7,
    deprecated: [],
    keywords: ['civil', 'bugfix'],
    summary: 'Stability fixes for the road design module.',
  },
  {
    product: 'Jobpac Connect',
    version: '2026 R2',
    title: 'Release Notes 2026 R2',
    date: '2026-03-05',
    status: 'GA',
    severity: 'Major',
    breaking: true,
    security: false,
    features: 8,
    bugs: 4,
    deprecated: ['Classic dashboard'],
    keywords: ['breaking', 'reporting'],
    summary: 'Retires the classic dashboard in favour of the new analytics hub.',
  },
  {
    product: 'App Xchange',
    version: 'May 14, 2026',
    title: "What's New in App Xchange May 14, 2026",
    date: '2026-05-14',
    status: 'GA',
    severity: 'Minor',
    breaking: false,
    security: false,
    features: 4,
    bugs: 3,
    deprecated: [],
    keywords: ['integration', 'api'],
    summary: 'New connectors and improved webhook reliability.',
  },
  {
    product: 'Trimble Pay',
    version: '5.7',
    title: "What's New in Trimble Pay 5.7 - May 2026",
    date: '2026-05-09',
    status: 'GA',
    severity: 'Minor',
    breaking: false,
    security: true,
    features: 3,
    bugs: 2,
    deprecated: [],
    keywords: ['payments', 'security'],
    summary: 'Adds ACH improvements and a security hardening pass.',
  },
].map(build);
